<script lang="ts">
import { defineComponent } from 'vue'

export enum Social {
  Facebook = 'facebook',
  Instagram = 'instagram',
  Youtube = 'youtube',
  X = 'x',
  Tiktok = 'tiktok',
}

export type SocialIconData = {
  icon: string
  text: string
  url: string
}

/**
 * Displays the social icons.
 */
export default defineComponent({
  props: {
    socials: {
      type: Array<Social>,
      default() {
        return []
      },
    },
    useColorIcons: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'sm', // Default to showing all 5 social icons
    },
  },
  computed: {
    socialsData(): Record<string, SocialIconData> {
      return {
        ...(this.$theme.socials.facebookUrl
          ? {
              facebook: {
                icon: `/assets/images/socials/facebook${this.useColorIcons ? '-coloured' : ''}.svg`,
                text: 'Facebook',
                url: this.$theme.socials.facebookUrl,
              },
            }
          : {}),
        ...(this.$theme.socials.instagramUrl
          ? {
              instagram: {
                icon: `/assets/images/socials/instagram${this.useColorIcons ? '-coloured' : ''}.svg`,
                text: 'Instagram',
                url: this.$theme.socials.instagramUrl,
              },
            }
          : {}),
        ...(this.$theme.socials.youtubeUrl
          ? {
              youtube: {
                icon: `/assets/images/socials/youtube${this.useColorIcons ? '-coloured' : ''}.svg`,
                text: 'YouTube',
                url: this.$theme.socials.youtubeUrl,
              },
            }
          : {}),
        ...(this.$theme.socials.xUrl
          ? {
              x: {
                icon: `/assets/images/socials/x${this.useColorIcons ? '-coloured' : ''}.svg`,
                text: 'X',
                url: this.$theme.socials.xUrl,
              },
            }
          : {}),
        ...(this.$theme.socials.tiktokUrl
          ? {
              tiktok: {
                icon: `/assets/images/socials/tiktok${this.useColorIcons ? '-coloured' : ''}.svg`,
                text: 'Tiktok',
                url: this.$theme.socials.tiktokUrl,
              },
            }
          : {}),
      }
    },
    iconSize(): string {
      return this.size === 'sm'
        ? 'h-8 w-8'
        : '@md/main:h-20 @md/main:w-20 h-8 w-8'
    },
  },
})
</script>

<template>
  <div class="flex w-fit flex-col text-center">
    <slot name="header" />

    <div class="flex justify-between gap-2">
      <a
        v-for="(social, key) in socialsData"
        :key="key"
        target="_blank"
        rel="noopener"
        :href="social.url"
        :aria-label="`Visit ${BUSINESS_NAME} on ${social.text}`"
        class="text-pure-white"
      >
        <img
          :src="social.icon"
          role="presentation"
          :alt="`${social.text} Logo`"
          class="text-pure-white"
          :class="iconSize"
        />
        <span class="sr-only">
          {{ social.text }}
        </span>
      </a>
    </div>

    <slot name="footer" />
  </div>
</template>
